import { produce } from 'immer';

import {
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST,
  CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS,
  CONTRACT_SETUP_BY_DRAFT_REQUEST,
  CONTRACT_SETUP_BY_DRAFT_SUCCESS,
  DOCUMENT_SETUP__REQUESTER_FIELDS_PAGE_OVER_DELETE,
  DOCUMENT_SETUP_CLEAR_ALL_STATE,
  DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_CHANGE,
  DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_DELETE,
  DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_INSERTED,
  TEMPLATE_INFORMATION_FETCH_SUCCESS,
} from '@app/store/actionTypes';
import {
  ContractSetupAction,
  contractSetupByDraftSuccess,
} from '@app/store/contract-setup/actions';
import ContractSetupRequesterFieldType from '@app/store/contract-setup/RequesterFieldType';
import { DocumentSetupAction } from '@app/store/documentSetup/actions';
import RequesterTextField from '@app/store/documentSetup/RequesterTextField';
import RequesterFieldType from '@app/store/RequesterFieldType';
import {
  scheduledDocumentPreviewDataFetched,
  setupTemplateByDraft,
  TemplateAction,
} from '@app/store/template/actions';
import RequesterInputType from '@app/store/template/RequesterInputType';
import TextAlign from '@app/store/TextAlign';

type DocumentSetupRequesterTextFieldsState = RequesterTextField[];

const initialState: DocumentSetupRequesterTextFieldsState = [];

export default function reducer(
  state: DocumentSetupRequesterTextFieldsState = initialState,
  action:
    | DocumentSetupAction
    | ContractSetupAction
    | TemplateAction
    | ReturnType<typeof contractSetupByDraftSuccess>
): DocumentSetupRequesterTextFieldsState {
  switch (action.type) {
    case CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_REQUEST:
    case CONTRACT_SETUP_BY_DRAFT_REQUEST:
    case DOCUMENT_SETUP_CLEAR_ALL_STATE:
      return initialState;
    case CONTRACT_SETUP__DOCUMENT_FIELDS_FETCH_SUCCESS:
      return produce(state, (draft) => {
        const { requesterFields } = action.payload;
        requesterFields.forEach((requesterField) => {
          const { id, positionX, positionY, positionZ, positionPage, sizeHeight, sizeWidth } =
            requesterField;
          if (requesterField.inputType === ContractSetupRequesterFieldType.TEXT_FIELD) {
            draft.push({
              id,
              pageNumber: positionPage,
              type: 'text',
              values: {
                dataLabel: requesterField.dataLabel,
                fontFamily: requesterField.textStyleFontType,
                fontSize: requesterField.textStyleFontSize,
                height: sizeHeight,
                text: requesterField.textInputContent,
                textAlign: requesterField.textStyleAlign,
                width: sizeWidth,
                x: positionX,
                y: positionY,
                z: positionZ,
              },
            });
          }
        });
      });
    case DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_CHANGE:
      return produce(state, (draft) => {
        const { id, values } = action.payload;
        const index = draft.findIndex((v) => v.id === id);
        draft[index].values = values;
      });
    case DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_DELETE:
      return produce(state, (draft) => {
        const { id } = action.payload;
        const index = draft.findIndex((v) => v.id === id);
        draft.splice(index, 1);
      });
    case DOCUMENT_SETUP_REQUESTER_TEXT_FIELD_INSERTED:
      return produce(state, (draft) => {
        const { id, pageNumber, values } = action.payload;

        /**
         * 필드 생성할 때 텍스트 정렬 기본값은 왼쪽(LEFT)입니다.
         */
        draft.push({
          id,
          pageNumber,
          type: 'text',
          values: { ...values, textAlign: TextAlign.LEFT },
        });
      });
    case scheduledDocumentPreviewDataFetched.type:
    case TEMPLATE_INFORMATION_FETCH_SUCCESS:
      return produce(state, (draft) => {
        const { requesterInputs } = action.payload;
        requesterInputs.forEach((requesterField) => {
          if (requesterField.fieldType === RequesterInputType.TEXT) {
            const { dataLabel, id, position, size, textInputContent, textInputTextStyle } =
              requesterField;
            draft.push({
              id,
              pageNumber: position.page,
              type: 'text',
              values: {
                dataLabel,
                fontFamily: textInputTextStyle.fontType,
                fontSize: textInputTextStyle.fontSize,
                height: size.height,
                text: textInputContent ?? '',
                textAlign: textInputTextStyle.align,
                width: size.width,
                x: position.x,
                y: position.y,
                z: position.z,
              },
            });
          }
        });
      });
    case CONTRACT_SETUP_BY_DRAFT_SUCCESS:
    case setupTemplateByDraft.type:
      return produce(state, (draft) => {
        action.payload.requesterInputs.forEach((field, index) => {
          if (field.type === RequesterFieldType.TEXT) {
            draft.push({
              id: field.id,
              pageNumber: field.position.page,
              type: 'text',
              values: {
                dataLabel: field.dataLabel,
                fontFamily: field.textStyle.font,
                fontSize: field.textStyle.size,
                height: field.size.height,
                text: field.value,
                textAlign: field.textStyle.align,
                width: field.size.width,
                x: field.position.x,
                y: field.position.y,
                z: field.position.z ?? index + 1,
              },
            });
          }
        });
      });
    case DOCUMENT_SETUP__REQUESTER_FIELDS_PAGE_OVER_DELETE:
      return produce(state, (draft) => {
        const { maxPage } = action.payload;
        return draft.filter((field) => field.pageNumber <= maxPage);
      });
    default:
      return state;
  }
}
